import html2canvas from 'html2canvas';
import jsPDF from 'jspdf'

class BrandedQuotePDFService {
  constructor() {
    this.pageWidth = 210; // A4 width in mm
    this.pageHeight = 297; // A4 height in mm
    this.margin = 20;
    this.contentWidth = this.pageWidth - (this.margin * 2);
    this.currentY = this.margin;
    this.pageBreakThreshold = this.pageHeight - 30; // Leave space at bottom
    
    // BRAND COLORS - MATCHED TO YOUR WEBSITE CSS
    this.colors = {
      // Primary: Dark charcoal (your --primary)
      primary: [34, 42, 55],
      // Secondary: Light gray (your --secondary)
      secondary: [248, 250, 252],
      // Text: Near black (your --foreground) 
      text: [31, 41, 55],
      // Muted: Medium gray (your --muted-foreground)
      muted: [115, 131, 140],
      // Border: Light border (your --border)
      border: [229, 235, 242],
      // White
      white: [255, 255, 255],
      // Accent: Very subtle green (much more muted)
      accent: [34, 72, 62]
    };
  }

  // PAGE BREAK MANAGEMENT
  checkPageBreak(pdf, neededSpace = 30) {
    if (this.currentY + neededSpace > this.pageBreakThreshold) {
      pdf.addPage();
      this.currentY = this.margin;
      return true;
    }
    return false;
  }

  // MAIN PDF GENERATION - FIXED FOR CONTENT OVERFLOW
  async generateQuotePDF(quote, acceptanceData) {
    try {
      console.log('🎨 Generating branded PDF matching website design for quote:', quote.uniqueId);
      
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      this.currentY = this.margin;
      
      // 1. Professional header
      await this.addBrandedHeader(pdf, quote);
      
      // 2. Status and greeting section
      this.addStatusAndGreeting(pdf, quote, acceptanceData);
      
      // 3. Client and quote information
      this.addClientQuoteInfo(pdf, quote, acceptanceData);
      
      // 4. Selected package details
      this.addSelectedPackage(pdf, quote, acceptanceData);
      
      // 5. Add-ons (if any)
      if (quote.interactiveTotal && quote.interactiveTotal > 0) {
        this.addInteractiveAddons(pdf, quote);
      }
      
      // 6. Total and signature
      this.addTotalAndSignature(pdf, quote, acceptanceData);
      
      // 7. Footer on all pages
      this.addBrandedFooter(pdf, quote);
      
      console.log('✅ Branded PDF generated successfully');
      return pdf;
      
    } catch (error) {
      console.error('❌ Error generating branded PDF:', error);
      throw new Error('Failed to generate professional PDF document');
    }
  }

  // BRANDED HEADER - MATCHES WEBSITE DESIGN
  async addBrandedHeader(pdf, quote) {
    // Clean header background (subtle gray)
    pdf.setFillColor(...this.colors.secondary);
    pdf.rect(0, 0, this.pageWidth, 45, 'F');
    
    // Add border line
    pdf.setDrawColor(...this.colors.border);
    pdf.setLineWidth(0.5);
    pdf.line(0, 45, this.pageWidth, 45);
    
    // Try to add logo (with better error handling)
    let logoSpace = 0;
    if (quote.owner?.avatarUrl) {
      try {
        console.log('📷 Loading business logo...');
        const logoDataUrl = await this.loadImageAsBase64(quote.owner.avatarUrl);
        
        if (logoDataUrl) {
          // Smaller, more refined logo placement
          pdf.addImage(logoDataUrl, 'JPEG', this.margin, 12, 20, 20);
          logoSpace = 25;
          console.log('✅ Logo loaded successfully');
        }
      } catch (logoError) {
        console.warn('⚠️ Logo failed to load, continuing without:', logoError.message);
        logoSpace = 0;
      }
    }
    
    // Business information (right-aligned, professional)
    // 🔧 FIX: Ensure business names are safe strings
    const businessName = String(quote.owner?.businessName || 'Professional Services').trim();
    const ownerName = String(quote.owner?.name || '').trim();
    
    // Business name
    pdf.setTextColor(...this.colors.text);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    const nameWidth = pdf.getTextWidth(businessName);
    pdf.text(businessName, this.pageWidth - this.margin - nameWidth, 20);
    
    // Owner name (smaller, muted)
    if (ownerName) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(...this.colors.muted);
      const ownerWidth = pdf.getTextWidth(ownerName);
      pdf.text(ownerName, this.pageWidth - this.margin - ownerWidth, 30);
    }
    
    this.currentY = 60;
    
    // Document title (matching website typography)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(24);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Quote Confirmation', this.margin + logoSpace, this.currentY);
    
    this.currentY += 20;
  }

  // STATUS AND GREETING - COMBINED, PROFESSIONAL SECTION
  addStatusAndGreeting(pdf, quote, acceptanceData) {
    this.checkPageBreak(pdf, 50);
    
    // Status indicator (subtle, professional)
    pdf.setFillColor(...this.colors.accent);
    pdf.setDrawColor(...this.colors.accent);
    pdf.roundedRect(this.margin, this.currentY, 22, 8, 4, 4, 'F');
    
    pdf.setTextColor(...this.colors.white);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.text('ACCEPTED', this.margin + 3, this.currentY + 5.5);
    
    // Quote ID (aligned right)
    pdf.setTextColor(...this.colors.muted);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    const quoteIdText = `Quote #${quote.uniqueId.substring(0, 8).toUpperCase()}`;
    const idWidth = pdf.getTextWidth(quoteIdText);
    pdf.text(quoteIdText, this.pageWidth - this.margin - idWidth, this.currentY + 5.5);
    
    this.currentY += 20;
    
    // Personalized greeting (clean, professional)
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 30, 4, 4, 'FD');
    
    pdf.setTextColor(...this.colors.text);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    // 🔧 FIX: Ensure client name is safe
    const safeClientName = String(quote.clientName || 'Client').trim();
    pdf.text(`Thank you, ${safeClientName}`, this.margin + 15, this.currentY + 12);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...this.colors.muted);
    // 🔧 FIX: Ensure email is safe
    const safeClientEmail = String(quote.clientEmail || 'client@example.com').trim();
    const confirmationText = `Your quote has been accepted. Confirmation sent to ${safeClientEmail}`;
    pdf.text(confirmationText, this.margin + 15, this.currentY + 22);
    
    this.currentY += 45;
  }

  // CLIENT AND QUOTE INFORMATION - CLEAN LAYOUT
  addClientQuoteInfo(pdf, quote, acceptanceData) {
    this.checkPageBreak(pdf, 70);
    
    const columnWidth = (this.contentWidth - 10) / 2;
    
    // Quote details (left column)
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(this.margin, this.currentY, columnWidth, 55, 4, 4, 'FD');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Quote Details', this.margin + 8, this.currentY + 12);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...this.colors.muted);
    
    let leftY = this.currentY + 20;
    pdf.text(`ID: ${quote.uniqueId.substring(0, 12).toUpperCase()}`, this.margin + 8, leftY);
    leftY += 6;
    pdf.text(`Created: ${this.formatDate(quote.createdAt)}`, this.margin + 8, leftY);
    leftY += 6;
    pdf.text(`Accepted: ${this.formatDate(acceptanceData.signedAt || new Date())}`, this.margin + 8, leftY);
    leftY += 6;
    pdf.text(`Status: Confirmed`, this.margin + 8, leftY);
    
    // Client information (right column)
    const rightX = this.margin + columnWidth + 10;
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(rightX, this.currentY, columnWidth, 55, 4, 4, 'FD');
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Client Information', rightX + 8, this.currentY + 12);
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...this.colors.muted);
    
    let rightY = this.currentY + 20;
    // 🔧 FIX: Ensure all text values are safe strings
    pdf.text(`Name: ${String(quote.clientName || 'Client').trim()}`, rightX + 8, rightY);
    rightY += 6;
    pdf.text(`Email: ${String(quote.clientEmail || 'client@example.com').trim()}`, rightX + 8, rightY);
    rightY += 6;
    const safeSignatureName = String(acceptanceData.clientSignatureName || acceptanceData.signatureName || 'Digital Signature').trim();
    pdf.text(`Signature: ${safeSignatureName}`, rightX + 8, rightY);
    rightY += 6;
    pdf.text(`Signed digitally`, rightX + 8, rightY);
    
    this.currentY += 70;
  }

  // SELECTED PACKAGE - CLEAN, PROFESSIONAL DESIGN
  addSelectedPackage(pdf, quote, acceptanceData) {
    this.checkPageBreak(pdf, 80);
    
    const selectedTier = acceptanceData.selectedTier;
    const tierData = quote.tiers[selectedTier];
    
    // Section header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Selected Package', this.margin, this.currentY);
    
    this.currentY += 15;
    
    // Package card (subtle, professional styling)
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 35, 4, 4, 'FD');
    
    // Package title and tier
    pdf.setTextColor(...this.colors.text);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.text(`${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Package`, this.margin + 12, this.currentY + 12);
    
    // Package description/title
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...this.colors.muted);
    // 🔧 FIX: Ensure title is always a valid string
    const safeTitle = String(tierData.title || '').trim() || 'Package';
    pdf.text(safeTitle, this.margin + 12, this.currentY + 22);
    
    // Price (right-aligned, clean)
    pdf.setTextColor(...this.colors.text);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    const priceText = this.formatPrice(tierData.price);
    const priceWidth = pdf.getTextWidth(priceText);
    pdf.text(priceText, this.pageWidth - this.margin - priceWidth - 12, this.currentY + 17);
    
    this.currentY += 45;
    
    // Package features (if available)
    if (tierData.bulletPoints && tierData.bulletPoints.length > 0) {
      this.checkPageBreak(pdf, tierData.bulletPoints.length * 6 + 20);
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(...this.colors.text);
      pdf.text('Included:', this.margin, this.currentY);
      
      this.currentY += 8;
      
      // Features list (clean, minimal)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(9);
      pdf.setTextColor(...this.colors.muted);
      
      tierData.bulletPoints.forEach(point => {
        if (point && point.trim()) {
          // Measure text width to prevent overflow
          const trimmedPoint = point.trim();
          const maxWidth = this.contentWidth - 15;
          const lines = pdf.splitTextToSize(trimmedPoint, maxWidth);
          
          lines.forEach((line, index) => {
            if (index === 0) {
              // First line with bullet
              pdf.text('•', this.margin + 5, this.currentY);
              pdf.text(line, this.margin + 12, this.currentY);
            } else {
              // Continuation lines (indented)
              pdf.text(line, this.margin + 12, this.currentY);
            }
            this.currentY += 5;
            
            // Check for page break on long feature lists
            this.checkPageBreak(pdf, 10);
          });
        }
      });
    }
    
    this.currentY += 10;
  }

  // ADD-ONS SECTION - CLEAN AND SIMPLE
  addInteractiveAddons(pdf, quote) {
    this.checkPageBreak(pdf, 40);
    
    // Section header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Additional Services', this.margin, this.currentY);
    
    this.currentY += 15;
    
    // Add-ons card (minimal styling)
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 25, 4, 4, 'FD');
    
    // Add-ons description
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...this.colors.muted);
    pdf.text('Additional products and services', this.margin + 12, this.currentY + 10);
    
    // Add-ons total (right-aligned)
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(...this.colors.text);
    const addonPrice = this.formatPrice(quote.interactiveTotal);
    const addonWidth = pdf.getTextWidth(addonPrice);
    pdf.text(addonPrice, this.pageWidth - this.margin - addonWidth - 12, this.currentY + 16);
    
    this.currentY += 35;
  }

  // TOTAL AND SIGNATURE - COMBINED PROFESSIONAL SECTION
  addTotalAndSignature(pdf, quote, acceptanceData) {
    this.checkPageBreak(pdf, 90);
    
    const selectedTier = acceptanceData.selectedTier;
    const tierPrice = quote.tiers[selectedTier].price;
    const interactiveTotal = quote.interactiveTotal || 0;
    const finalTotal = tierPrice + interactiveTotal;
    
    // Total section header
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Total Investment', this.margin, this.currentY);
    
    this.currentY += 15;
    
    // Breakdown (if add-ons exist)
    if (interactiveTotal > 0) {
      // Package line
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(...this.colors.muted);
      pdf.text(`${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} Package:`, this.margin + 10, this.currentY);
      
      const packagePriceText = this.formatPrice(tierPrice);
      const packageWidth = pdf.getTextWidth(packagePriceText);
      pdf.text(packagePriceText, this.pageWidth - this.margin - packageWidth - 10, this.currentY);
      
      this.currentY += 6;
      
      // Add-ons line
      pdf.text('Additional Services:', this.margin + 10, this.currentY);
      
      const addonPriceText = this.formatPrice(interactiveTotal);
      const addonWidth = pdf.getTextWidth(addonPriceText);
      pdf.text(addonPriceText, this.pageWidth - this.margin - addonWidth - 10, this.currentY);
      
      this.currentY += 10;
      
      // Separator line
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(...this.colors.border);
      pdf.line(this.margin + 10, this.currentY, this.pageWidth - this.margin - 10, this.currentY);
      
      this.currentY += 8;
    }
    
    // Total amount (clean, prominent)
    pdf.setFillColor(...this.colors.text);
    pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 22, 4, 4, 'F');
    
    pdf.setTextColor(...this.colors.white);
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.text('Total Amount:', this.margin + 15, this.currentY + 14);
    
    const totalText = this.formatPrice(finalTotal);
    const totalWidth = pdf.getTextWidth(totalText);
    pdf.text(totalText, this.pageWidth - this.margin - totalWidth - 15, this.currentY + 14);
    
    this.currentY += 35;
    
    // Digital signature section
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(...this.colors.text);
    pdf.text('Digital Signature', this.margin, this.currentY);
    
    this.currentY += 12;
    
    // Signature details (clean layout)
    pdf.setFillColor(...this.colors.secondary);
    pdf.setDrawColor(...this.colors.border);
    pdf.roundedRect(this.margin, this.currentY, this.contentWidth, 30, 4, 4, 'FD');
    
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.setTextColor(...this.colors.muted);
    pdf.text('Signed by:', this.margin + 12, this.currentY + 10);
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(...this.colors.text);
    const signerName = acceptanceData.clientSignatureName || acceptanceData.signatureName || 'Digital Signature';
    // 🔧 FIX: Ensure signerName is always a valid string
    const safeSignerName = String(signerName || 'Digital Signature').trim();
    pdf.text(safeSignerName, this.margin + 12, this.currentY + 20);
    
    // Signature date (right-aligned)
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(...this.colors.muted);
    const dateText = this.formatDate(acceptanceData.signedAt || new Date());
    const dateWidth = pdf.getTextWidth(dateText);
    pdf.text(dateText, this.pageWidth - this.margin - dateWidth - 12, this.currentY + 20);
    
    this.currentY += 40;
    
    // Legal note (minimal)
    pdf.setFont('helvetica', 'italic');
    pdf.setFontSize(8);
    pdf.setTextColor(...this.colors.muted);
    const legalText = 'Digitally signed and legally binding under electronic signature laws.';
    pdf.text(legalText, this.margin, this.currentY);
    
    this.currentY += 15;
  }

  // BRANDED FOOTER - MINIMAL AND CLEAN
  addBrandedFooter(pdf, quote) {
    const pageCount = pdf.internal.getNumberOfPages();
    
    // Check if user is on free plan
    const isFreePlan = quote.owner?.subscription_status === 'trialing' || 
                       !quote.owner?.subscription_status || 
                       quote.owner?.subscription_status === 'free' ||
                       quote.owner?.plan_type === 'free';
    
    // Add footer to all pages
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      
      const footerY = this.pageHeight - 20;
      
      // Minimal footer line
      pdf.setDrawColor(...this.colors.border);
      pdf.setLineWidth(0.5);
      pdf.line(this.margin, footerY - 5, this.pageWidth - this.margin, footerY - 5);
      
      // Footer content (minimal, professional)
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(...this.colors.muted);
      
      // 🔧 FIX: Ensure business name is safe
      const safeBusinessName = String(quote.owner?.businessName || 'Professional Services').trim();
      pdf.text(`Generated by ${safeBusinessName}`, this.margin, footerY + 3);
      
      // Page number and date (right-aligned)
      const dateText = this.formatDate(new Date());
      let pageText = `Page ${i} of ${pageCount} • ${dateText}`;
      
      // Add "Powered by QuoteTier" for free users, unless they've purchased white-label unlock
      if (isFreePlan && !quote.owner?.whiteLabelUnlocked) {
        pageText = `${pageText} • Powered by QuoteTier`;
      }
      
      const rightWidth = pdf.getTextWidth(pageText);
      pdf.text(pageText, this.pageWidth - this.margin - rightWidth, footerY + 3);
    }
  }

  // IMPROVED IMAGE LOADING WITH BETTER ERROR HANDLING
  async loadImageAsBase64(imageUrl) {
    try {
      console.log('🖼️ Loading business logo...');
      
      return new Promise((resolve, reject) => {
        const img = new Image();
        
        // Try with CORS first, fallback to no CORS
        img.crossOrigin = 'anonymous';
        
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            // Constrain image size for PDF performance
            const maxSize = 200;
            let { width, height } = img;
            
            if (width > maxSize || height > maxSize) {
              const ratio = Math.min(maxSize / width, maxSize / height);
              width *= ratio;
              height *= ratio;
            }
            
            canvas.width = width;
            canvas.height = height;
            
            // Draw image
            ctx.drawImage(img, 0, 0, width, height);
            
            // Convert to base64 with compression
            const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
            console.log('✅ Logo processed successfully');
            resolve(dataUrl);
          } catch (canvasError) {
            console.warn('⚠️ Canvas processing failed:', canvasError.message);
            reject(canvasError);
          }
        };
        
        img.onerror = () => {
          // Try without CORS as fallback
          const imgFallback = new Image();
          imgFallback.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              
              const maxSize = 200;
              let { width, height } = imgFallback;
              
              if (width > maxSize || height > maxSize) {
                const ratio = Math.min(maxSize / width, maxSize / height);
                width *= ratio;
                height *= ratio;
              }
              
              canvas.width = width;
              canvas.height = height;
              ctx.drawImage(imgFallback, 0, 0, width, height);
              
              const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
              console.log('✅ Logo loaded (fallback mode)');
              resolve(dataUrl);
            } catch (error) {
              console.warn('⚠️ Fallback processing failed:', error.message);
              reject(error);
            }
          };
          
          imgFallback.onerror = (fallbackError) => {
            console.warn('⚠️ Logo loading failed completely:', fallbackError);
            reject(new Error('Logo loading failed'));
          };
          
          imgFallback.src = imageUrl;
        };
        
        img.src = imageUrl;
        
        // Timeout after 3 seconds (faster for better UX)
        setTimeout(() => {
          reject(new Error('Logo loading timeout'));
        }, 3000);
      });
    } catch (error) {
      console.warn('⚠️ Error loading logo:', error.message);
      throw error;
    }
  }

  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price || 0);
  }

  formatDate(dateString) {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  formatDateTime(dateString) {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  }

  async generatePDFBlob(quote, acceptanceData) {
    const el = document.getElementById('quote-print');
    const canvas = await html2canvas(el, { scale: 2 });
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', [canvas.width, canvas.height]);
    pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
    return pdf.output('blob');
  }

  // PUBLIC METHODS
  async downloadQuotePDF(quote, acceptanceData, filename = null) {
    try {
      console.log('📄 Generating professional PDF download...');
      const pdf = await this.generateQuotePDF(quote, acceptanceData);
      const defaultFilename = `${quote.owner.businessName?.replace(/[^a-z0-9]/gi, '-') || 'quote'}-${quote.uniqueId}-accepted.pdf`;
      pdf.save(filename || defaultFilename);
      console.log('✅ PDF downloaded successfully');
      return true;
    } catch (error) {
      console.error('❌ Error downloading PDF:', error);
      throw error;
    }
  }

  async generatePDFBlob(quote, acceptanceData) {
    try {
      console.log('📄 Generating PDF blob for email...');
      const el = document.getElementById('quote-print');         // Tailwind-styled div
      const canvas = await html2canvas(el, { scale: 2 });        // snapshot DOM
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', [canvas.width, canvas.height]);
      pdf.addImage(imgData, 'PNG', 0, 0, canvas.width, canvas.height);
      const blob = pdf.output('blob');
      console.log('✅ PDF blob generated successfully');
      return blob;
    } catch (error) {
      console.error('❌ Error generating PDF blob:', error);
      throw error;
    }
  }
}

// Export singleton instance with improved branding
export const quotePDFService = new BrandedQuotePDFService();
export default quotePDFService;